import { useState } from 'react';
import { Lightbulb, Bug, Heart, Send, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'suggestion', label: 'Sugestão', Icon: Lightbulb, color: 'from-amber-400 to-orange-500',   ring: 'ring-amber-300'   },
  { key: 'bug',        label: 'Bug',      Icon: Bug,       color: 'from-rose-400 to-red-500',       ring: 'ring-rose-300'    },
  { key: 'praise',     label: 'Elogio',   Icon: Heart,     color: 'from-pink-400 to-fuchsia-500',   ring: 'ring-pink-300'    },
];

const MAX = 1000;

/**
 * Student-facing dialog to send feedback (suggestion / bug / praise) to the
 * teacher. Persists via POST /api/student/feedback and shows a toast on the
 * appropriate outcome. Rate-limited at 5/24h by the backend — respected here
 * by surfacing the 429 message verbatim.
 */
const StudentFeedbackDialog = ({ open, onOpenChange }) => {
  const [category, setCategory] = useState('suggestion');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const reset = () => { setCategory('suggestion'); setMessage(''); };
  const close = () => { onOpenChange(false); setTimeout(reset, 200); };

  const canSend = message.trim().length >= 5 && !sending;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/student/feedback`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category, message: message.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data?.detail || 'Não foi possível enviar seu feedback');
        return;
      }
      toast.success('Feedback enviado! Obrigado 💛');
      close();
    } catch (e) {
      toast.error('Erro de conexão — tente novamente');
    } finally {
      setSending(false);
    }
  };

  const remaining = MAX - message.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit text-xl flex items-center gap-2">
            <Send className="w-5 h-5 text-primary" />
            Enviar Feedback
          </DialogTitle>
          <DialogDescription>
            Sua opinião ajuda o professor a melhorar o Centro. Escolha uma categoria e escreva sua mensagem.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Category pills */}
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-black mb-2">Categoria</p>
            <div className="grid grid-cols-3 gap-2" data-testid="feedback-categories">
              {CATEGORIES.map(({ key, label, Icon, color, ring }) => {
                const active = category === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setCategory(key)}
                    data-testid={`feedback-cat-${key}`}
                    className={`flex flex-col items-center gap-1 rounded-xl border-2 p-3 transition-all ${
                      active
                        ? `bg-gradient-to-br ${color} text-white border-transparent shadow-lg ring-2 ${ring} -translate-y-0.5`
                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <Icon className="w-5 h-5" strokeWidth={2.4} />
                    <span className="text-xs font-outfit font-black uppercase tracking-wider">{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Message field */}
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-black mb-2">Mensagem</p>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MAX))}
              placeholder="Descreva sua sugestão, bug ou elogio..."
              rows={5}
              data-testid="feedback-message"
              className="w-full border-2 border-slate-200 rounded-xl p-3 text-sm resize-none focus:border-primary focus:ring-1 focus:ring-primary/40 focus:outline-none"
            />
            <div className="flex items-center justify-between text-[11px] text-slate-400 mt-1">
              <span>Mínimo de 5 caracteres.</span>
              <span className={remaining < 50 ? 'text-amber-600 font-bold' : ''}>{remaining} restantes</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t">
          <Button variant="ghost" onClick={close} disabled={sending} data-testid="feedback-cancel">
            <X className="w-4 h-4" /> Cancelar
          </Button>
          <Button onClick={handleSend} disabled={!canSend} data-testid="feedback-submit">
            <Send className="w-4 h-4" />
            {sending ? 'Enviando...' : 'Enviar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StudentFeedbackDialog;
